import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject, OnInit, OnDestroy,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
} from '@angular/forms';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectSearchComponent } from 'app/ui/mat-select-search/mat-select-search.component';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { CustomAlertService } from 'app/modules/shared/services/alert.service';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { DistinctItemsPaginationService } from '../../distinct-items-pagination.service';
import { SaiDialogHeaderComponent } from 'app/ui/sai-dialog-header/sai-dialog-header.component';
import {
    DistinctItemsColumnStore,
    applyDistinctFetchResult,
    beginDistinctFetch,
    createDistinctItemsColumnStore,
    failDistinctFetch,
    initDistinctColumn,
    pruneDistinctColumns,
    seedSelectedDistinctOptions,
} from '../../distinct-items-pagination.util';

@Component({
    selector: 'app-filter-new-tracker',
    standalone: true,
    imports: [
        SaiDialogHeaderComponent,
        CommonModule,
        CdkScrollable,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatTooltipModule,
        MatSelectSearchComponent,
        TranslocoModule,
    ],
    templateUrl: './filter-new-tracker.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './filter-new-tracker.component.scss',
})
export class FilterNewTrackerComponent implements OnInit, OnDestroy {
    private readonly _distinctPagination = inject(DistinctItemsPaginationService);

    dateForm: FormGroup;
    dimensionCtrl = new FormControl<any[]>([]);
    startDateCtrl = new FormControl<Date | null>(null);
    endDateCtrl = new FormControl<Date | null>(null);
    dimensionColumns: any[] = [];
    distinctStore: DistinctItemsColumnStore = createDistinctItemsColumnStore();
    dropdownFormControls: { [key: string]: FormControl } = {};
    selectedValues: { [key: string]: string[] } = {};
    selectedDiminsionsLength = 0;
    protected _unsubscribeAll = new Subject<void>();
    lang: string = 'en';

    constructor(
        private _alertService: CustomAlertService,
        private fb: FormBuilder,
        private _changeDetectorRef: ChangeDetectorRef,
        private _dialogRef: MatDialogRef<FilterNewTrackerComponent>,
        private _translocoService: TranslocoService,
        @Inject(MAT_DIALOG_DATA)
        public data: {
            dimensionColumns: any[];
            filters: { column?: string; values?: string[]; columnName?: string; item?: string[] }[];
            table: string;
            dataSourceId: string;
            isExternal: boolean;
        }
    ) {}

    get distinctItems(): DistinctItemsColumnStore['items'] {
        return this.distinctStore.items;
    }

    get distinctHasMore(): DistinctItemsColumnStore['hasMore'] {
        return this.distinctStore.hasMore;
    }

    get distinctLoadingMore(): DistinctItemsColumnStore['loadingMore'] {
        return this.distinctStore.loadingMore;
    }

    ngOnInit(): void {
        this._translocoService.langChanges$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((res) => {
                this.lang = res;
                this._changeDetectorRef.markForCheck();
            });

        this.dimensionColumns = this.data.dimensionColumns || [];

        const preSelectedDimensions = [
            ...new Set(
                this.data.filters
                    .map(f => this.getFilterColumn(f))
                    .filter((c): c is string => c != null)
            ),
        ];

        this.dimensionCtrl.setValue(preSelectedDimensions, {
            emitEvent: false,
        });
        this.selectedDiminsionsLength = preSelectedDimensions.length;

        if (preSelectedDimensions.length > 0) {
            preSelectedDimensions.forEach((columnKey) => {
                this.initDistinctColumn(columnKey);
                this.fetchDistinctPage(columnKey, true, () => {
                    this.applyPreselectedValues();
                    this._changeDetectorRef.markForCheck();
                });
            });
        }

        this.dimensionCtrl.valueChanges
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                this.onSelectDimension();
            });

        this._changeDetectorRef.markForCheck();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    onSelectDimension(): void {
        let isAddingDimension = false;

        if (this.selectedDiminsionsLength < (this.dimensionCtrl.value?.length ?? 0)) {
            this.selectedDiminsionsLength = this.dimensionCtrl.value.length;
            isAddingDimension = true;
        } else {
            this.selectedDiminsionsLength--;
            isAddingDimension = false;
        }

        const selectedDimensions = this.dimensionCtrl.value ?? [];

        if (isAddingDimension) {
            const addedKeys = selectedDimensions.filter(
                (key: string) => !this.distinctStore.items[key]
            );
            for (const key of addedKeys) {
                this.initDistinctColumn(key);
                this.fetchDistinctPage(key, true);
            }
        } else {
            const selectedSet = new Set(selectedDimensions);
            pruneDistinctColumns(this.distinctStore, selectedSet);

            Object.keys(this.dropdownFormControls).forEach((key) => {
                if (!selectedSet.has(key)) {
                    delete this.dropdownFormControls[key];
                }
            });

            this.initializeDropdownControls();
            this.applyPreselectedValues();
            this._changeDetectorRef.markForCheck();
        }
    }

    onDistinctItemSearch(key: string, keyword: string): void {
        if (!this.distinctStore.pages[key]) {
            this.initDistinctColumn(key);
        }
        this.distinctStore.pages[key].keyword = keyword;
        this.fetchDistinctPage(key, true);
    }

    onDistinctItemLoadMore(key: string): void {
        this.fetchDistinctPage(key, false);
    }

    private fetchDistinctPage(
        key: string,
        reset: boolean,
        onComplete?: () => void
    ): void {
        if (!this.data.table || !this.data.dataSourceId) {
            return;
        }
        if (!this.distinctStore.pages[key]) {
            this.initDistinctColumn(key);
        }
        const page = this.distinctStore.pages[key];
        if (!beginDistinctFetch(page, reset, this.distinctStore, key)) {
            return;
        }
        this._changeDetectorRef.markForCheck();

        this._distinctPagination
            .fetchPage(
                'tracker',
                key,
                page,
                reset,
                {
                    table: this.data.table,
                    dataSource: this.data.dataSourceId,
                    isExternal: this.data.isExternal,
                },
                reset ? [] : this.distinctStore.items[key] ?? []
            )
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (result) => {
                    applyDistinctFetchResult(
                        page,
                        this.distinctStore,
                        key,
                        result
                    );
                    const selected =
                        this.dropdownFormControls[key]?.value ??
                        this.getFilterValues(
                            this.data.filters.find(
                                f => this.getFilterColumn(f) === key
                            ) ?? {}
                        );
                    this.distinctStore.items[key] = seedSelectedDistinctOptions(
                        this.distinctStore.items[key],
                        Array.isArray(selected) ? selected : []
                    );
                    this.initializeDropdownControls();
                    onComplete?.();
                    this._changeDetectorRef.markForCheck();
                },
                error: (error) => {
                    failDistinctFetch(page, this.distinctStore, key);
                    this._alertService.failedMessage(
                        error?.error?.message ||
                            'Failed to fetch distinct items.'
                    );
                    onComplete?.();
                    this._changeDetectorRef.markForCheck();
                },
            });
    }

    private initDistinctColumn(key: string): void {
        initDistinctColumn(this.distinctStore, key);
        this.initializeDropdownControls();
    }

    private applyPreselectedValues(): void {
        const valuesByColumn: Record<string, string[]> = {};
        this.data.filters.forEach((filter) => {
            const col = this.getFilterColumn(filter);
            const vals = this.getFilterValues(filter);
            if (col == null) return;
            if (!valuesByColumn[col]) valuesByColumn[col] = [];
            valuesByColumn[col].push(...vals);
        });
        Object.keys(valuesByColumn).forEach((col) => {
            const selected = [...new Set(valuesByColumn[col])];
            if (this.distinctStore.items[col]) {
                this.distinctStore.items[col] = seedSelectedDistinctOptions(
                    this.distinctStore.items[col],
                    selected
                );
            }
            const control = this.dropdownFormControls[col];
            if (control) {
                control.setValue(selected);
            }
        });
    }

    private getFilterColumn(f: any): string | null {
        const c = f?.column ?? f?.columnName;
        return c != null && c !== '' ? String(c) : null;
    }

    private getFilterValues(f: any): string[] {
        const v = f?.values ?? f?.item;
        return Array.isArray(v) ? v : [];
    }

    initializeDropdownControls(): void {
        Object.keys(this.distinctStore.items).forEach((field) => {
            if (!this.dropdownFormControls[field]) {
                this.dropdownFormControls[field] = new FormControl([]);
            }
        });
    }

    applyFilters(): void {
        const selectedDimensions = this.dimensionCtrl.value ?? [];

        const selectedItems = selectedDimensions
            .map((columnName: string) => {
                let selected =
                    this.dropdownFormControls[columnName]?.value || [];
                const hasSelectAll = selected.includes('__select_all__');
                if (hasSelectAll && this.distinctStore.items[columnName]) {
                    selected = this.distinctStore.items[columnName].map(
                        o => o.id
                    );
                } else {
                    selected = selected.filter(v => v !== '__select_all__');
                }
                return {
                    columnName,
                    item: selected,
                };
            })
            .filter(item => item.item.length > 0);

        this._dialogRef.close({ selectedItems });
    }

    closeDialog(): void {
        this._dialogRef.close();
    }
}
